import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'

const services = [
  {
    num: '01',
    title: 'Tech Leadership Search',
    tagline: 'CXO · VP · Director-level placement',
    description:
      'We identify and place transformational technology leaders — from VPs and Engineering Heads to Chief Data Officers and CTOs — across the world\'s most ambitious GCCs and product companies.',
    capabilities: ['VP of Engineering', 'Head of Data Science', 'Chief Technology Officer', 'Director of Product', 'Principal / Staff Engineer'],
    href: '/services',
  },
  {
    num: '02',
    title: 'GCC Talent Setup & Scaling',
    tagline: 'Centre inception to 200+ headcount',
    description:
      'From founding hire to capability scale — we partner with GCCs at every stage of their journey. Assess the operating model, establish the leadership core, build the capability teams, and scale with intent.',
    capabilities: ['Founding Leader Placement', 'Team Architecture Advisory', 'Capability Ramp Planning', '4-Factor Assessment', 'Compensation Benchmarking'],
    href: '/gcc-practice',
  },
]

export default function ServicesSection() {
  return (
    <section
      data-testid="services-section"
      id="services"
      className="py-32 md:py-40 px-6 md:px-12 lg:px-24 bg-[#F6F7F9]"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-20"
        >
          <p className="text-[10px] tracking-[0.35em] uppercase text-[#0A0A0A]/30 mb-5 font-body font-semibold">
            What We Do
          </p>
          <h2 className="text-[clamp(2.5rem,5vw,5rem)] font-heading font-black tracking-tighter leading-none text-[#0A0A0A]">
            Two practices.{' '}
            <span className="font-light text-[#16365F]">One standard.</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2">
          {services.map((service, i) => (
            <motion.div
              key={service.num}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: i * 0.15 }}
              data-testid={`service-card-${i + 1}`}
              className="p-8 md:p-12 border border-[#E5E7EB] bg-white group hover:border-[#0B1F3A] transition-all duration-400"
            >
              <p
                className="font-heading font-black text-[#E5E7EB] leading-none mb-8 select-none"
                style={{ fontSize: 'clamp(4rem,8vw,6rem)' }}
              >
                {service.num}
              </p>
              <p className="text-[9px] tracking-[0.3em] uppercase text-[#16365F] mb-3 font-body font-semibold">
                {service.tagline}
              </p>
              <h3 className="text-2xl md:text-3xl font-heading font-black text-[#0A0A0A] mb-5 tracking-tight leading-tight">
                {service.title}
              </h3>
              <p className="text-sm font-body text-[#0A0A0A]/55 leading-relaxed mb-8">
                {service.description}
              </p>
              <ul className="space-y-2 mb-10">
                {service.capabilities.map(cap => (
                  <li key={cap} className="flex items-center gap-3">
                    <div className="w-1 h-1 bg-[#16365F] flex-shrink-0" />
                    <span className="text-xs font-body text-[#0A0A0A]/50">{cap}</span>
                  </li>
                ))}
              </ul>
              <Link
                to={service.href}
                data-testid={`service-explore-${i + 1}`}
                className="inline-flex items-center gap-2 text-[10px] font-body font-semibold tracking-[0.15em] uppercase text-[#0A0A0A] border-b border-[#0A0A0A]/30 pb-0.5 group-hover:text-[#0B1F3A] group-hover:border-[#0B1F3A] transition-colors duration-200"
              >
                Explore Service <ArrowRight size={11} />
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
